import React, { useState, useCallback } from 'react';
import { useCanvasAnimation } from '../useCanvasAnimation';

interface StarIgnitionProps {
  width?: number | string;
  height?: number | string;
}

const IgnitionFallbackCanvas: React.FC<{ size: number }> = ({ size }) => {
  const draw = useCallback((ctx: CanvasRenderingContext2D, frameCount: number) => {
    ctx.fillStyle = '#0F172A';
    ctx.fillRect(0, 0, size, size);

    const centerX = size / 2;
    const centerY = size / 2;
    
    const time = (frameCount % 200) / 200; // 0 to 1 loop
    
    // Core
    ctx.beginPath();
    ctx.arc(centerX, centerY, 30, 0, Math.PI * 2);
    ctx.fillStyle = '#EA580C'; // Dark orange protostar
    ctx.fill();

    if (time > 0.4 && time < 0.6) {
      // Ignition flash
      const flashAlpha = Math.sin((time - 0.4) * Math.PI * 5); // 0 to 1 back to 0
      ctx.beginPath();
      ctx.arc(centerX, centerY, 150, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255, 255, 255, ${flashAlpha})`;
      ctx.fill();
    }

    if (time >= 0.5) {
      // Main sequence star
      const starPulse = Math.sin(frameCount * 0.1) * 2;
      const gradient = ctx.createRadialGradient(centerX, centerY, 10, centerX, centerY, 50 + starPulse);
      gradient.addColorStop(0, '#FFFFFF');
      gradient.addColorStop(0.3, '#FDE047');
      gradient.addColorStop(1, 'rgba(253, 224, 71, 0)');
      
      ctx.fillStyle = gradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, 50 + starPulse, 0, Math.PI * 2);
      ctx.fill();
    }

  }, [size]);

  const canvasRef = useCanvasAnimation(draw, size);

  return <canvas ref={canvasRef} style={{ borderRadius: '12px', width: '100%', height: '100%' }} />;
};

export const StarIgnition: React.FC<StarIgnitionProps> = ({ width = '100%', height = 400 }) => {
  const [hasError, setHasError] = useState(false);
  const [showIframe, setShowIframe] = useState(true);

  return (
    <div style={{ width, height, position: 'relative', display: 'flex', justifyContent: 'center', alignItems: 'center', background: '#0F172A', borderRadius: '12px', overflow: 'hidden' }}>
      {showIframe && !hasError ? (
        <iframe
          width="100%"
          height="100%"
          src="https://www.youtube.com/embed/Ojc2Hj1a8nU?autoplay=1&mute=1&loop=1&playlist=Ojc2Hj1a8nU&controls=0&modestbranding=1"
          title="Star Ignition"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          onError={() => setHasError(true)}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
        />
      ) : (
        <IgnitionFallbackCanvas size={Number(height)} />
      )}
      
      {!hasError && showIframe && (
        <div style={{ position: 'absolute', bottom: 10, right: 10, zIndex: 10 }}>
          <button 
            onClick={() => setShowIframe(false)}
            style={{ background: 'rgba(0,0,0,0.5)', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '10px', cursor: 'pointer' }}>
            Switch to 2D Fallback
          </button>
        </div>
      )}
    </div>
  );
};
